import { useEffect, useRef, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { AlertCircle, ArrowUp, Check, Loader2, Plus, Sparkles, Square, X } from 'lucide-react'

import { Button } from 'components/ui/button'
import { Sheet } from 'components/ui/sheet'
import { Textarea } from 'components/ui/textarea'
import { cn } from 'lib/utils'
import { aiClearError, aiNewConversation, type AiChatMessage, type AiState, type AiToolActivity } from 'slices/aiSlice'

import { useAskMdt } from '../context/AskMdtProvider'
import { useRouteAssetContext } from '../hooks/useRouteAssetContext'
import { suggestionsFor } from '../suggestions'
import { AskMdtMarkdown } from './AskMdtMarkdown'
import { PlanCard } from './PlanCard'

/**
 * The Ask MDT conversation (MYD-621): a right-hand sheet, the full screen on a
 * phone, with the messages and a composer.
 *
 * Enter sends; Shift+Enter is a new line. While an answer streams the send
 * button becomes Stop. Closing the sheet stops it too (AskMdtProvider).
 */

/** What a lookup is called while it runs. Unknown tools get a generic word. */
const TOOL_LABELS: Record<string, string> = {
	list_evaluations: 'evaluations',
	get_evaluation: 'the evaluation',
	list_sites: 'sites',
	get_site: 'the site',
	list_criteria: 'scoring criteria',
	get_scoring_summary: 'scores',
	get_site_financials: 'financials',
	compare_financials: 'financials',
	list_risks: 'risks',
	list_pros_cons: 'pros and cons',
	list_approvals: 'approvals',
	get_approval_readiness: 'approval readiness',
	list_portfolios: 'portfolios',
	get_portfolio: 'the portfolio',
	list_locations: 'locations',
	get_location: 'the location',
	list_projects: 'projects',
	get_project: 'the project',
	list_tasks: 'tasks',
}

/** Tools that do something other than look up (MYD-623): said as they are. */
const TOOL_PHRASES: Record<string, string> = {
	resolve_place: 'Finding the place',
	resolve_person: 'Checking your partners',
	resolve_entity: 'Finding the record',
	resolve_type: 'Matching the type',
	propose_plan: 'Drafting a plan',
}

export const toolLabel = (name: string) => TOOL_PHRASES[name] ?? `Looking up ${TOOL_LABELS[name] ?? 'your data'}`

function ToolChips({ tools }: { tools: AiToolActivity[] }) {
	if (!tools.length) return null
	return (
		<ul className="mb-2 flex flex-wrap gap-1.5" aria-label="Lookups">
			{tools.map(tool => (
				<li
					key={tool.id}
					className={cn(
						'inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[11px]',
						tool.status === 'error' ? 'border-destructive/30 text-destructive' : 'border-gray-200 text-gray-600',
					)}
				>
					{tool.status === 'running' ? <Loader2 className="h-3 w-3 animate-spin" /> : tool.status === 'error' ? <AlertCircle className="h-3 w-3" /> : <Check className="h-3 w-3 text-success" />}
					{toolLabel(tool.name)}
				</li>
			))}
		</ul>
	)
}

function MessageBubble({ message }: { message: AiChatMessage }) {
	if (message.role === 'user') {
		return (
			<div className="flex justify-end">
				<p className="max-w-[85%] whitespace-pre-wrap rounded-2xl rounded-br-sm bg-primary px-3 py-2 text-sm text-primary-foreground">{message.text}</p>
			</div>
		)
	}
	const waiting = message.status === 'streaming' && !message.text && !message.tools.length
	return (
		<div className="flex gap-2">
			<Sparkles className="mt-1 h-4 w-4 shrink-0 text-primary" aria-hidden="true" />
			<div className="min-w-0 flex-1">
				<ToolChips tools={message.tools} />
				{waiting ? (
					<p className="flex items-center gap-1.5 text-sm text-muted-foreground">
						<Loader2 className="h-3.5 w-3.5 animate-spin" /> Thinking…
					</p>
				) : (
					message.text && <AskMdtMarkdown text={message.text} />
				)}
				{message.status === 'stopped' && <p className="mt-1 text-xs text-muted-foreground">Stopped.</p>}
				{message.planId && <PlanCard planId={message.planId} />}
			</div>
		</div>
	)
}

export function AskMdtSheet() {
	const askMdt = useAskMdt()
	const dispatch = useDispatch<any>()
	const routeContext = useRouteAssetContext()
	const { messages, status, error } = useSelector((state: { ai: AiState }) => state.ai)
	const [draft, setDraft] = useState('')
	const endRef = useRef<HTMLDivElement>(null)
	const inputRef = useRef<HTMLTextAreaElement>(null)
	const open = askMdt?.mode === 'panel'
	const sending = status === 'sending'
	const lastText = messages.at(-1)?.text

	// Follow the answer as it is written.
	useEffect(() => {
		if (open) endRef.current?.scrollIntoView?.({ block: 'end' })
	}, [open, messages.length, lastText])

	useEffect(() => {
		if (open) requestAnimationFrame(() => inputRef.current?.focus())
	}, [open])

	if (!askMdt) return null

	const send = (text = draft) => {
		if (!text.trim() || sending) return
		askMdt.ask(text)
		setDraft('')
	}

	return (
		<Sheet open={open} onClose={askMdt.close} side="right" className="w-full sm:w-[460px] sm:max-w-[100vw]">
			<header className="flex h-14 shrink-0 items-center gap-2 border-b border-gray-200 px-4">
				<Sparkles className="h-4 w-4 text-primary" aria-hidden="true" />
				<h2 className="flex-1 text-base font-semibold text-gray-900">Ask MDT</h2>
				<Button
					variant="ghost"
					onClick={() => {
						askMdt.stop()
						dispatch(aiNewConversation())
					}}
					disabled={!messages.length}
					title="Start a new conversation"
				>
					<Plus className="mr-1 h-3.5 w-3.5" /> New chat
				</Button>
				<button type="button" onClick={askMdt.close} aria-label="Close Ask MDT" className="rounded p-1.5 text-gray-500 hover:bg-gray-100">
					<X className="h-4 w-4" />
				</button>
			</header>

			<div className="flex-1 space-y-4 overflow-y-auto px-4 py-4" aria-live="polite" aria-busy={sending}>
				{messages.length === 0 ? (
					<div className="space-y-3 pt-6 text-center">
						<Sparkles className="mx-auto h-8 w-8 text-primary/60" aria-hidden="true" />
						<p className="text-sm text-gray-600">Ask about your locations, projects, portfolios, evaluations and tasks.</p>
						<div className="flex flex-col items-stretch gap-2 pt-2">
							{suggestionsFor(routeContext).map(prompt => (
								<button
									key={prompt}
									type="button"
									onClick={() => send(prompt)}
									className="rounded-lg border border-gray-200 px-3 py-2 text-left text-sm text-gray-700 hover:border-primary hover:bg-primary/5"
								>
									{prompt}
								</button>
							))}
						</div>
					</div>
				) : (
					messages.map(message => <MessageBubble key={message.id} message={message} />)
				)}
				<div ref={endRef} />
			</div>

			{error && (
				<div role="alert" className="mx-4 mb-2 flex items-start gap-2 rounded-md border border-destructive/30 bg-destructive/5 px-3 py-2 text-sm text-destructive">
					<AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
					<p className="flex-1">{error.message}</p>
					<button type="button" onClick={() => dispatch(aiClearError())} aria-label="Dismiss" className="rounded p-0.5 hover:bg-destructive/10">
						<X className="h-3.5 w-3.5" />
					</button>
				</div>
			)}

			<form
				className="flex shrink-0 items-end gap-2 border-t border-gray-200 p-3"
				onSubmit={event => {
					event.preventDefault()
					send()
				}}
			>
				<Textarea
					ref={inputRef}
					value={draft}
					rows={1}
					maxLength={4000}
					onChange={event => setDraft(event.target.value)}
					onKeyDown={event => {
						if (event.key === 'Enter' && !event.shiftKey && !event.nativeEvent.isComposing) {
							event.preventDefault()
							send()
						}
					}}
					placeholder="Ask a question…"
					aria-label="Message Ask MDT"
					className="max-h-40 min-h-[40px] flex-1 resize-none"
				/>
				{sending ? (
					<Button type="button" variant="ghost" isOutline onClick={askMdt.stop} aria-label="Stop answering" title="Stop">
						<Square className="h-4 w-4" />
					</Button>
				) : (
					<Button type="submit" disabled={!draft.trim()} aria-label="Send">
						<ArrowUp className="h-4 w-4" />
					</Button>
				)}
			</form>
		</Sheet>
	)
}

export default AskMdtSheet
